import React from "react";
import { GetCookieFunction } from "../utils/util";

function Help() {
    const userType = GetCookieFunction("User").type;
    const isAdmin = GetCookieFunction("User").admin === 1 || userType === "centeradmin" || userType === "executive";
    const isDoctor = userType === "doctor";
    const isExecutive = userType === "executive";

    return (
        <div className="help-page">
            <h1>User Guide</h1>
            <p className="help-intro">Welcome to the SBF EHR System. This guide covers the features available to your role and how to use them.</p>

            <nav className="help-toc">
                <h2>Contents</h2>
                <ul>
                    <li><a href="#getting-started">Getting Started</a></li>
                    <li><a href="#common-features">Common Features</a></li>
                    <li><a href="#patient-treatment">Patient Treatment Workflow</a></li>
                    {isDoctor && <li><a href="#doctor-features">Doctor Features</a></li>}
                    {isAdmin && <li><a href="#admin-features">Administrative Features</a></li>}
                    <li><a href="#patient-registration">Patient Registration</a></li>
                </ul>
            </nav>

            {/* Getting Started */}
            <section id="getting-started">
                <h2>Getting Started</h2>

                <h3>Logging In</h3>
                <ol>
                    <li>Enter your <strong>email address</strong> and <strong>password</strong></li>
                    <li>Select your <strong>center</strong> from the dropdown (this only applies to executives — other users are automatically assigned to their center)</li>
                    <li>Click <strong>Sign In</strong></li>
                </ol>

                <h3>Navigation</h3>
                <ul>
                    <li>Use the <strong>hamburger menu</strong> (☰) on the top-left for quick access to all features</li>
                    <li>The <strong>Home</strong> page shows dashboard cards for common tasks</li>
                    <li>When viewing a patient, the <strong>patient navigation bar</strong> lets you switch between pages</li>
                </ul>

                <h3>Changing Your Password</h3>
                <ol>
                    <li>Go to <strong>Change Password</strong> from the menu</li>
                    <li>Enter your current password</li>
                    <li>Enter and confirm your new password</li>
                    <li>Click <strong>Change Password</strong></li>
                </ol>
            </section>

            {/* Common Features */}
            <section id="common-features">
                <h2>Common Features</h2>

                <h3>Home Dashboard</h3>
                <p>Your home page shows today's patient counts (completed, in treatment, waiting) and quick action cards for common tasks.</p>

                <h3>Lookup a Patient</h3>
                <ol>
                    <li>Click <strong>Lookup a Patient</strong></li>
                    <li>Optionally filter by first/last name</li>
                    <li>Use checkboxes to show only checked-in or in-treatment patients</li>
                    <li>Click <strong>Search</strong></li>
                </ol>
                <p>Results show each patient's name, age, and status. Click a patient's name to view their full profile.</p>

                <h3>Patient Profile</h3>
                <p>The profile page displays all patient information in cards: personal details, medical history, allergies, medications, surgeries, dialysis order, visit history, and more. Each card has an edit icon to update that section.</p>
            </section>

            {/* Patient Treatment Workflow */}
            <section id="patient-treatment">
                <h2>Patient Treatment Workflow</h2>
                <p>The typical daily workflow for a dialysis patient:</p>

                <h3>1. Check-In</h3>
                <p>Record pre-treatment vitals and physical assessment:</p>
                <ul>
                    <li><strong>Vitals:</strong> weight, sitting/standing BP, body temperature, respiratory rate, O₂ saturation, pulse</li>
                    <li><strong>Physical exam:</strong> pulse regularity, anemia, edema, skin condition, psychic function, lung sound, neck veins, heart sound, AVF, catheter</li>
                    <li><strong>Assessment notes</strong> and electronic signature</li>
                </ul>

                <h3>2. Start Treatment</h3>
                <p>Review the dialysis order and begin the session:</p>
                <ul>
                    <li>The system displays the current dialysis prescription (set by the doctor)</li>
                    <li>Enter the patient's <strong>current weight</strong></li>
                    <li>Click <strong>Start Treatment</strong></li>
                </ul>
                <p>If no dialysis order exists, {isDoctor ? "use the edit link to add one." : "contact the patient's doctor to have one added."}</p>

                <h3>3. Record Vitals</h3>
                <p>During treatment, record hemodialysis parameters (can be done multiple times):</p>
                <ul>
                    <li>Access flow (Good/Poor)</li>
                    <li>Blood pressure, blood flow rate, dialysate flow rate</li>
                    <li>Arterial pressure, venous pressure, TMP, UF rate, fluid removed</li>
                    <li>Optional: heparin, NS flush, comments</li>
                    <li>Safety check and electronic signature required</li>
                </ul>

                <h3>4. Check-Out</h3>
                <p>Complete the visit with post-treatment vitals:</p>
                <ul>
                    <li>Weight, sitting/standing BP, body temperature, respiratory rate, O₂ saturation, pulse</li>
                    <li>Assessment notes and electronic signature</li>
                </ul>
            </section>

            {/* Doctor Features */}
            {isDoctor && (
                <section id="doctor-features">
                    <h2>Doctor Features</h2>

                    <h3>Dialysis Order</h3>
                    <p>Set the dialysis prescription for a patient:</p>
                    <ul>
                        <li><strong>Treatment parameters:</strong> frequency, duration, blood flow rate, dry weight, dialysate flow rate, dialyzer type</li>
                        <li><strong>Electrolytes:</strong> sodium, potassium, calcium, bicarbonate (mEq/L)</li>
                        <li>Optional comments</li>
                    </ul>

                    <h3>Treatment Notes</h3>
                    <p>A structured 8-step review process:</p>
                    <ol>
                        <li><strong>Blood Pressure</strong> — review BP history, classify as Stable/High/Low</li>
                        <li><strong>Volume Assessment</strong> — review weight trends</li>
                        <li><strong>Dialysis Adequacy</strong> — review Kt/V and URR targets</li>
                        <li><strong>Access Assessment</strong> — review access flow status</li>
                        <li><strong>Anemia Management</strong> — review hemoglobin and iron levels</li>
                        <li><strong>Bone Mineral Management</strong> — review phosphorus, calcium, PTH</li>
                        <li><strong>Nutrition Assessment</strong> — review albumin and potassium</li>
                        <li><strong>Signature</strong> — confirm and sign the notes</li>
                    </ol>
                    <p>Each step shows relevant lab history and allows you to record observations with optional comments.</p>
                </section>
            )}

            {/* Admin Features */}
            {isAdmin && (
                <section id="admin-features">
                    <h2>Administrative Features</h2>

                    <h3>Create a Staff Account</h3>
                    <ol>
                        <li>Enter personal details: name, gender, DOB, timezone</li>
                        <li>Enter contact and address information</li>
                        <li>Select the <strong>user type</strong>: Doctor, Nurse, or Center Admin{isExecutive && " (or Executive)"}</li>
                        <li>Assign a center{isExecutive && " (not required for executives)"}</li>
                        <li>Optionally grant administrative privileges</li>
                        <li>Set an initial password</li>
                        <li>Click <strong>Create Staff Account</strong></li>
                    </ol>

                    <h3>Manage Staff</h3>
                    <p>Search and manage existing staff members:</p>
                    <ul>
                        <li>Search by first/last name{isExecutive && " and filter by center"}</li>
                        <li>View staff type, admin status, active/inactive status</li>
                        <li>Click the edit icon to update a staff member's details, role, or status</li>
                    </ul>

                    <h3>Patient Status Management</h3>
                    <p>When editing a patient's personal profile, administrators see a <strong>Patient Status</strong> section:</p>
                    <ul>
                        <li><strong>Current Center</strong> — reassign the patient to a different center</li>
                        <li><strong>Patient Account Active</strong> — activate or deactivate the patient</li>
                    </ul>

                    <h3>Reports</h3>
                    <p>View center statistics showing the number of active patients and active staff at your current center.</p>
                </section>
            )}

            {/* Patient Registration */}
            <section id="patient-registration">
                <h2>Patient Registration</h2>
                <p>When registering a new patient, the system guides you through a multi-step intake:</p>
                <ol>
                    <li><strong>Personal Details</strong> — name, gender, age, phone, email, address</li>
                    <li><strong>Patient Info</strong> — referral doctor, next of kin, blood group, income, medical details</li>
                    <li><strong>Past Medical History</strong> — select pre-existing conditions</li>
                    <li><strong>Family &amp; Social History</strong> — document background</li>
                    <li><strong>Review of Systems</strong> — systematic symptom review (14 body systems)</li>
                    <li><strong>Allergies</strong> — record known allergies</li>
                    <li><strong>Medications</strong> — record current medications</li>
                    <li><strong>Surgical History</strong> — record past procedures</li>
                    <li><strong>Assessment &amp; Treatment Plan</strong> — initial clinical assessment</li>
                    {isDoctor && <li><strong>Dialysis Order</strong> — set treatment prescription</li>}
                </ol>
                <p>Each step saves independently. If you stop partway through, your data is preserved and you can continue later from the patient's profile page.</p>
            </section>

            <section id="roles-permissions">
                <h2>Roles &amp; Permissions</h2>
                <p>The system has four staff roles with different access levels:</p>
                <table className="help-table">
                    <thead>
                        <tr>
                            <th>Feature</th>
                            <th>Doctor</th>
                            <th>Nurse</th>
                            <th>Center Admin</th>
                            <th>Executive</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr><td>View patient profile</td><td>✓</td><td>✓</td><td>✓</td><td>✓</td></tr>
                        <tr><td>Patient lookup</td><td>✓</td><td>✓</td><td>✓</td><td>✓</td></tr>
                        <tr><td>Register new patient</td><td>✓</td><td>✓</td><td>✓</td><td>✓</td></tr>
                        <tr><td>Edit patient personal profile</td><td>✓</td><td>✓</td><td>✓</td><td>✓</td></tr>
                        <tr><td>Edit patient info (referral, blood type, etc.)</td><td>✓</td><td>✓</td><td>✓</td><td>✓</td></tr>
                        <tr><td>Medical history forms (PMH, family, ROS)</td><td>✓</td><td>✓</td><td>—</td><td>—</td></tr>
                        <tr><td>Allergies / Medications / Surgeries</td><td>✓</td><td>✓</td><td>—</td><td>—</td></tr>
                        <tr><td>Assessment &amp; Plan</td><td>✓</td><td>✓</td><td>—</td><td>—</td></tr>
                        <tr><td>Check-in / Check-out</td><td>✓</td><td>✓</td><td>—</td><td>—</td></tr>
                        <tr><td>Start treatment / Record vitals</td><td>✓</td><td>✓</td><td>—</td><td>—</td></tr>
                        <tr><td>Dialysis order (edit)</td><td>✓</td><td>—</td><td>—</td><td>—</td></tr>
                        <tr><td>Treatment notes (8-step)</td><td>✓</td><td>—</td><td>—</td><td>—</td></tr>
                        <tr><td>Create / manage staff accounts</td><td>Admin only</td><td>Admin only</td><td>✓</td><td>✓</td></tr>
                        <tr><td>Reports</td><td>Admin only</td><td>Admin only</td><td>✓</td><td>✓</td></tr>
                        <tr><td>Patient status (center/active)</td><td>—</td><td>—</td><td>✓</td><td>✓</td></tr>
                        <tr><td>Cross-center staff search</td><td>—</td><td>—</td><td>—</td><td>✓</td></tr>
                        <tr><td>Create executive accounts</td><td>—</td><td>—</td><td>—</td><td>✓</td></tr>
                    </tbody>
                </table>
            </section>

            <div className="help-footer">
                <p>Need further assistance? Contact your center administrator.</p>
            </div>
        </div>
    );
}

export default Help;
